import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SESSION_META, STATUS_META, pipsToUsd, fmtUSD, fmtPct } from "@/lib/calc";
import { useChallenge } from "@/context/ChallengeContext";
import { Sunrise, Sun, Moon } from "lucide-react";

const ICONS = { 1: Sunrise, 2: Sun, 3: Moon };

export default function SessionCard({ day, session, sessionData, capitalBefore, targetPct }) {
  const { saveSession } = useChallenge();
  const meta = SESSION_META[session];
  const Icon = ICONS[session];

  const [status, setStatus] = useState(sessionData?.status || "not_started");
  const [lot, setLot] = useState(sessionData?.lot ?? "");
  const [entry, setEntry] = useState(sessionData?.entry ?? "");
  const [sl, setSl] = useState(sessionData?.sl ?? "");
  const [tp, setTp] = useState(sessionData?.tp ?? "");
  const [result, setResult] = useState(sessionData?.result_usd ?? "");
  const [completed, setCompleted] = useState(sessionData?.completed || false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setStatus(sessionData?.status || "not_started");
    setLot(sessionData?.lot ?? "");
    setEntry(sessionData?.entry ?? "");
    setSl(sessionData?.sl ?? "");
    setTp(sessionData?.tp ?? "");
    setResult(sessionData?.result_usd ?? "");
    setCompleted(sessionData?.completed || false);
  }, [sessionData]);

  // auto-calc result when status / lot / pips change (unless manually edited to non-empty by user interplay)
  const autoResult = (st, l, tpPips, slPips) => {
    if (st === "profit") return pipsToUsd(l, tpPips);
    if (st === "loss") return -pipsToUsd(l, slPips);
    if (st === "be") return 0;
    return "";
  };

  const onStatusChange = (v) => {
    setStatus(v);
    const r = autoResult(v, Number(lot) || 0, Number(tp) || 0, Number(sl) || 0);
    if (r !== "") setResult(Math.round(r * 100) / 100);
    else if (v === "skipped" || v === "not_started") setResult("");
  };

  const onLotChange = (e) => {
    const l = e.target.value;
    setLot(l);
    const r = autoResult(status, Number(l) || 0, Number(tp) || 0, Number(sl) || 0);
    if (r !== "") setResult(Math.round(r * 100) / 100);
  };

  const meterColor = STATUS_META[status]?.dot || "#64748b";
  const targetUsd = (capitalBefore * targetPct) / 100;
  const capitalAfter =
    status === "profit" || status === "loss" || status === "be"
      ? capitalBefore + (Number(result) || 0)
      : capitalBefore;

  const save = async () => {
    setBusy(true);
    try {
      await saveSession({
        day,
        session,
        status,
        lot: lot === "" ? null : Number(lot),
        entry: entry === "" ? null : Number(entry),
        sl: sl === "" ? null : Number(sl),
        tp: tp === "" ? null : Number(tp),
        result_usd: result === "" ? null : Number(result),
        completed,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glow-card rounded-2xl p-5" data-testid={`session-card-${session}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Icon className="text-violet-400" size={22} />
          <div>
            <div className="font-display font-bold text-lg leading-none">{meta.label}</div>
            <div className="text-[10px] tracking-widest text-demon-muted mt-1">{meta.sub}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: meterColor, boxShadow: `0 0 8px ${meterColor}` }} />
          <span className={`text-xs font-semibold ${STATUS_META[status]?.color}`}>
            {STATUS_META[status]?.label}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-4 font-mono text-sm">
        <div className="flex justify-between border-b border-violet-900/20 py-1.5">
          <span className="text-demon-muted text-xs">Kapitał przed</span>
          <span className="text-white">{fmtUSD(capitalBefore)}</span>
        </div>
        <div className="flex justify-between border-b border-violet-900/20 py-1.5">
          <span className="text-demon-muted text-xs">Cel %</span>
          <span className="text-violet-300">{fmtPct(targetPct)}</span>
        </div>
        <div className="flex justify-between border-b border-violet-900/20 py-1.5">
          <span className="text-demon-muted text-xs">Cel $</span>
          <span className="text-white">{fmtUSD(targetUsd)}</span>
        </div>
        <div className="flex justify-between border-b border-violet-900/20 py-1.5">
          <span className="text-demon-muted text-xs">Kapitał po</span>
          <span className={capitalAfter >= capitalBefore ? "text-emerald-400" : "text-red-400"}>
            {fmtUSD(capitalAfter)}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <div className="col-span-2 sm:col-span-3">
          <Label className="text-[10px] tracking-widest text-demon-muted">STATUS</Label>
          <Select value={status} onValueChange={onStatusChange}>
            <SelectTrigger
              data-testid={`session-status-${session}`}
              className="mt-1 bg-black/50 border-violet-900/40 h-10 text-white"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-demon-surface2 border-violet-900/40 text-white">
              {Object.entries(STATUS_META).map(([k, v]) => (
                <SelectItem key={k} value={k}>
                  {v.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {[
          ["Lot", lot, onLotChange, `session-lot-${session}`],
          ["Entry", entry, (e) => setEntry(e.target.value), `session-entry-${session}`],
          ["SL", sl, (e) => setSl(e.target.value), `session-sl-${session}`],
          ["TP", tp, (e) => setTp(e.target.value), `session-tp-${session}`],
          ["Wynik $", result, (e) => setResult(e.target.value), `session-result-${session}`],
        ].map(([label, val, on, tid]) => (
          <div key={tid}>
            <Label className="text-[10px] tracking-widest text-demon-muted">{label}</Label>
            <Input
              data-testid={tid}
              type="number"
              step="any"
              value={val}
              onChange={on}
              className="mt-1 bg-black/50 border-violet-900/40 h-10 text-white font-mono focus-visible:ring-violet-500"
            />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-demon-muted cursor-pointer">
          <Checkbox
            data-testid={`session-completed-${session}`}
            checked={completed}
            onCheckedChange={(v) => setCompleted(!!v)}
            className="border-violet-500/50 data-[state=checked]:bg-violet-600"
          />
          Tura zakończona
        </label>
        <Button
          data-testid={`session-submit-button-${session}`}
          onClick={save}
          disabled={busy}
          className="font-display font-semibold bg-gradient-to-r from-violet-700 to-violet-500 border-0 shadow-[0_0_16px_rgba(168,85,247,0.3)]"
        >
          ZAPISZ WYNIK
        </Button>
      </div>
    </div>
  );
}
